import {
    DataLayerEventName,
    SavedArticlesFilterEvent,
    SearchEventArgs,
    SearchFilterEvent,
} from '@news-mono/web-common'
import { getPublishedSince, TIME_FILTER_PARAM } from './TimeFilter'
import { RESULT_SORT_PARAM, relevanceSortOption } from './ResultSort'
import { FilterQueryParam } from './FilterQueryParam'

export const TOPIC_FILTER_PARAM = 'topics'
export const LOCATION_FILTER_PARAM = 'locations'
const STATE_FILTER_PARAM = 'state'

// Returns the available topic filters depending on the theme kind.
// For `sevennews`, the list is simplified and tailored to main sections.
// For any other (currently only The Nightly) this list loosely reflects quickLinks found in appsthenightlysrcappnavigation.ts
// It currently excludes "The Front Dore" link
export const getArticleTopics = (themeKind: string): FilterQueryParam[] => {
    if (themeKind === 'sevennews') {
        return [
            { displayName: 'All', paramName: 'all' },
            { displayName: 'Business', paramName: 'business' },
            { displayName: 'Sport', paramName: 'sport' },
            { displayName: 'Lifestyle', paramName: 'lifestyle' },
            { displayName: 'Entertainment', paramName: 'entertainment' },
            { displayName: 'Politics', paramName: 'politics' },
            { displayName: 'Motoring', paramName: 'motoring' },
        ]
    }

    return [
        { displayName: 'All', paramName: 'all' },
        { displayName: 'Politics', paramName: 'politics' },
        { displayName: 'Australia', paramName: 'australia' },
        { displayName: 'Business', paramName: 'business' },
        { displayName: 'Opinion', paramName: 'opinion' },
        { displayName: 'Culture', paramName: 'culture' },
        { displayName: 'Society', paramName: 'society' },
        { displayName: 'Lifestyle', paramName: 'lifestyle' },
        { displayName: 'Sport', paramName: 'sport' },
        { displayName: 'World', paramName: 'world' },
    ]
}

export const getLocationTopics = (themeKind: string): FilterQueryParam[] => {
    if (themeKind !== 'sevennews') return []

    return [
        { displayName: 'All', paramName: 'all' },
        { displayName: 'New South Wales', paramName: 'news/nsw' },
        { displayName: 'Victoria', paramName: 'news/vic' },
        { displayName: 'Queensland', paramName: 'news/qld' },
        { displayName: 'Western Australia', paramName: 'news/wa' },
        { displayName: 'South Australia', paramName: 'news/sa' },
        { displayName: 'Tasmania', paramName: 'news/tas' },
        { displayName: 'Northern Territory', paramName: 'news/nt' },
        { displayName: 'Australian Capital Territory', paramName: 'news/act' },
        { displayName: 'World', paramName: 'news/world' },
    ]
}

export const getDefaultTopicOption = (themeKind: string): string => {
    const topics = getArticleTopics(themeKind)
    return topics[0].displayName
}

export const getDefaultLocationOption = (themeKind: string): string => {
    if (themeKind !== 'sevennews') return ''

    const locations = getLocationTopics(themeKind)
    return locations[0].displayName
}

export type FilterEvent = SearchFilterEvent | SavedArticlesFilterEvent

export interface FilterEventProps {
    onEvent: (event: FilterEvent) => void
    dataLayerEventName:
        | DataLayerEventName.searchFilter
        | DataLayerEventName.savedArticlesFilter
}

const topicEventName = 'Topics'
const resultSortEventName = 'Result Sort'
const timeFilterEventName = 'Time Filter'
const stateFilterEventName = 'State Filter'
const locationFilterEventName = 'Location Filter'

export const filterParams = [
    TIME_FILTER_PARAM,
    RESULT_SORT_PARAM,
    TOPIC_FILTER_PARAM,
    LOCATION_FILTER_PARAM,
    STATE_FILTER_PARAM,
]

export const getEventName = (param: string) => {
    switch (param) {
        case TIME_FILTER_PARAM:
            return timeFilterEventName
        case RESULT_SORT_PARAM:
            return resultSortEventName
        case TOPIC_FILTER_PARAM:
            return topicEventName
        case LOCATION_FILTER_PARAM:
            return locationFilterEventName
        case STATE_FILTER_PARAM:
            return stateFilterEventName
    }
}

export const getSearchEventArgs = (
    searchTerm: string,
    searchParams: URLSearchParams,
    themeKind: string,
    defaultTopicOption?: string,
    defaultLocationOption?: string,
): SearchEventArgs => {
    const publishedSince = getPublishedSince(
        searchParams.get(TIME_FILTER_PARAM) ?? undefined,
        themeKind,
    )
    const topics = searchParams.get(TOPIC_FILTER_PARAM) ?? defaultTopicOption
    const location =
        searchParams.get(LOCATION_FILTER_PARAM) ?? defaultLocationOption
    const sortBy =
        searchParams.get(RESULT_SORT_PARAM) ?? relevanceSortOption.paramName

    return {
        searchTerm,
        topics,
        location,
        sortBy,
        publishedSince,
    }
}
